import api from './api';
import { API_BASE_URL } from '../config';

// --- Session Management Helpers ---

export const getAccessToken = () => {
  // Tokens are now stored in httpOnly cookies, so we can't access them via JS.
  // We'll return a placeholder or null, as the browser handles this automatically.
  return null;
};

export const getRefreshToken = () => {
  return null;
};

export const getCurrentUser = () => {
  const userStr = sessionStorage.getItem('paperPortalUser') || localStorage.getItem('paperPortalUser');
  try {
    return userStr ? JSON.parse(userStr) : null;
  } catch (e) {
    return null;
  }
};

export const isLoggedIn = () => {
  return !!getCurrentUser(); // Use presence of user data as login indicator
};

export const isVerifiedEditorial = (user = getCurrentUser()) => {
  if (!user) return false;
  const permissions = user.permissions || {};

  return (
    !!user.is_editorial_board ||
    user.is_editorial_board === 1 ||
    user.is_editorial_board === true ||
    !!user.is_verified_sub_eb ||
    user.is_verified_sub_eb === 1 ||
    user.is_verified_sub_eb === true ||
    !!permissions.is_verified_sub_eb ||
    !!permissions.is_editorial_board ||
    permissions.is_verified_sub_eb === 1 ||
    permissions.is_editorial_board === 1
  );
};

export const isVerifiedReviewer = (user = getCurrentUser()) => {
  if (!user) return false;
  const permissions = user.permissions || {};
  return (
    !!user.is_reviewer ||
    user.is_reviewer === 1 ||
    user.is_reviewer === true ||
    user.role === 'reviewer' ||
    user.role_code === 'reviewer' ||
    !!permissions.is_reviewer ||
    permissions.is_reviewer === 1
  );
};

export const storeSession = (user, accessToken = null, refreshToken = null, rememberMe = false) => {
  // Always clear specific keys first to avoid stale permissions
  sessionStorage.removeItem('paperPortalUser');
  localStorage.removeItem('paperPortalUser');
  localStorage.removeItem('accessToken'); // Cleanup legacy
  localStorage.removeItem('refreshToken'); // Cleanup legacy

  sessionStorage.setItem('paperPortalUser', JSON.stringify(user));

  if (rememberMe) {
    localStorage.setItem('paperPortalUser', JSON.stringify(user));
  }
};

export const clearSession = () => {
  sessionStorage.clear();
  localStorage.clear();
  window.dispatchEvent(new Event('userLogout'));
};

// --- API Calls ---

// Fetch available roles
export const fetchRoles = async () => {
  try {
    const response = await api.get('/meta/roles');
    return response.data.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

// Fetch roles available for login
export const fetchLoginRoles = async () => {
  try {
    const response = await api.get('/meta/roles/login');
    return response.data.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

// Login user
export const loginUser = async (loginData) => {
  try {
    const response = await api.post('/auth/login', loginData);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

// Send OTP for phone-based login
export const sendLoginOtp = async (phone_number, role_id) => {
  try {
    const response = await api.post('/auth/login/send-otp', { phone_number, role_id });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

// Verify OTP and complete phone-based login
export const verifyLoginOtp = async (phone_number, role_id, otp, remember_me = false) => {
  try {
    const response = await api.post('/auth/login/verify-otp', { phone_number, role_id, otp, remember_me });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

// --- Token Refresh Lock ---
let isRefreshing = false;
let refreshSubscribers = [];

const onTokenRefreshed = () => {
  refreshSubscribers.forEach((callback) => callback.resolve(true));
  refreshSubscribers = [];
};

const onTokenRefreshFailed = (error) => {
  refreshSubscribers.forEach((callback) => callback.reject(error));
  refreshSubscribers = [];
};

const addRefreshSubscriber = (resolve, reject) => {
  refreshSubscribers.push({ resolve, reject });
};

// Refresh Access Token
// NOTE: This intentionally uses fetch or it could use api with different config to avoid interceptor loop
export const refreshAccessToken = async () => {
  // If already refreshing, return a promise that resolves/rejects when done
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      addRefreshSubscriber(resolve, reject);
    });
  }

  isRefreshing = true;

  try {
    // Calling refresh-token without passing token in body (uses cookie)
    const response = await fetch(`${API_BASE_URL}/auth/refresh-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      // Note: We need to include credentials in fetch too if using it directly
      credentials: 'include',
      body: JSON.stringify({}),
    });

    const data = await response.json();

    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        console.warn('Refresh token invalid/expired. Logging out...');
        clearSession();
      }
      throw new Error(data.message || 'Wrong credentials. Please login again.');
    }

    if (data.success) {
      onTokenRefreshed();
      return data;
    }

    const error = new Error(data.message || 'Token refresh failed');
    onTokenRefreshFailed(error);
    return data;
  } catch (error) {
    console.error('Token refresh error:', error);
    onTokenRefreshFailed(error);
    throw error;
  } finally {
    isRefreshing = false;
  }
};

export const logoutUser = async () => {
  try {
    // Call logout (backend clears cookies)
    const response = await api.post('/auth/logout', {});
    clearSession();
    return response.data;
  } catch (error) {
    clearSession();
    throw error.response?.data || error;
  }
};

// Check if phone number exists
export const checkPhone = async (phone_number) => {
  try {
    const response = await api.post('/auth/check-phone', { phone_number });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

// Send OTP to phone number
export const sendPhoneOtp = async (phone_number) => {
  try {
    const response = await api.post('/auth/phone/send-otp', { phone_number });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

// Verify phone OTP
export const verifyPhoneOtp = async (phone_number, otp) => {
  try {
    const response = await api.post('/auth/phone/verify-otp', { phone_number, otp });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

// Check if email exists
export const checkEmail = async (email) => {
  try {
    const response = await api.post('/auth/check-email', { email });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

// Send OTP to email
export const sendEmailOtp = async (email) => {
  try {
    const response = await api.post('/auth/email/send-otp', { email });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

// Verify email OTP
export const verifyEmailOtp = async (email, otp) => {
  try {
    const response = await api.post('/auth/email/verify-otp', { email, otp });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

// Register user
export const registerUser = async (registrationData) => {
  try {
    const response = await api.post('/auth/register', registrationData);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

// Reset password using phone OTP
export const resetPassword = async (phone_number, new_password) => {
  try {
    const response = await api.post('/auth/reset-password', { phone_number, new_password });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};